import { useEffect } from 'react'
import { pathLengthKm } from '../../utils/structures'
import { useMapStore, mapState } from '../../store/mapStore'

// Ruler mode: same click-to-place-points mechanic as infrastructure drawing,
// but free-standing — no region ownership, no cost, nothing sent to the server.
// Just a distance readout (per-segment labels here; the sidebar shows the total).
export function useRuler(map, { rulerMode, rulerPath, onRulerPointMove, onRulerPointDelete }) {
  useEffect(() => {
    if (!map.current || !map.current.getLayer('regions-fill')) return

    const hasPath = rulerMode && rulerPath && rulerPath.length > 0
    const lineFc = {
      type: 'FeatureCollection',
      features: hasPath && rulerPath.length >= 2
        ? [{
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: rulerPath.map((p) => [p.lngLat.lng, p.lngLat.lat]),
            },
            properties: {},
          }]
        : [],
    }
    const pointFc = {
      type: 'FeatureCollection',
      features: hasPath
        ? rulerPath.map((p, index) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [p.lngLat.lng, p.lngLat.lat] },
            properties: { index },
          }))
        : [],
    }
    // One label per segment, at its midpoint — always "as drawn" (per-segment
    // distance is unambiguous; only the sidebar's *total* has a crow-flies option).
    const labelFc = {
      type: 'FeatureCollection',
      features: hasPath && rulerPath.length >= 2
        ? rulerPath.slice(1).map((p, i) => {
            const a = rulerPath[i].lngLat
            const b = p.lngLat
            const km = pathLengthKm([[a.lng, a.lat], [b.lng, b.lat]])
            return {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [(a.lng + b.lng) / 2, (a.lat + b.lat) / 2] },
              properties: { label: `${km.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} km` },
            }
          })
        : [],
    }

    const lineSrc = map.current.getSource('ruler-path-line')
    if (!lineSrc) {
      map.current.addSource('ruler-path-line', { type: 'geojson', data: lineFc })
      map.current.addLayer({
        id: 'ruler-path-line',
        type: 'line',
        source: 'ruler-path-line',
        paint: {
          'line-color': '#4FD0E0',
          'line-width': 3,
          'line-dasharray': [1, 1],
        },
      })
    } else {
      lineSrc.setData(lineFc)
    }
    if (map.current.getLayer('ruler-path-line')) {
      map.current.setLayoutProperty('ruler-path-line', 'visibility', hasPath ? 'visible' : 'none')
    }

    const pointSrc = map.current.getSource('ruler-path-points')
    if (!pointSrc) {
      map.current.addSource('ruler-path-points', { type: 'geojson', data: pointFc })
      map.current.addLayer({
        id: 'ruler-path-points',
        type: 'circle',
        source: 'ruler-path-points',
        paint: {
          'circle-radius': 6,
          'circle-color': '#4FD0E0',
          'circle-stroke-color': '#1a1a1a',
          'circle-stroke-width': 2,
        },
      })

      // Same correction gestures as infra path points: drag to move, right-click to delete.
      map.current.on('mousedown', 'ruler-path-points', (e) => {
        if (!mapState().rulerModeActive || !onRulerPointMove) return
        if (e.originalEvent.button !== 0) return
        if (!e.features || e.features.length === 0) return
        const index = e.features[0].properties.index
        e.preventDefault()
        useMapStore.setState({ structDragActive: true })
        map.current.getCanvas().style.cursor = 'grabbing'

        const onMove = (ev) => onRulerPointMove(index, ev.lngLat)
        const onUp = () => {
          map.current.off('mousemove', onMove)
          useMapStore.setState({ structDragActive: false })
          useMapStore.setState({ suppressNextMapClick: Date.now() })
          map.current.getCanvas().style.cursor = 'crosshair'
        }
        map.current.on('mousemove', onMove)
        map.current.once('mouseup', onUp)
      })

      map.current.on('contextmenu', 'ruler-path-points', (e) => {
        if (!mapState().rulerModeActive || !onRulerPointDelete) return
        if (!e.features || e.features.length === 0) return
        e.preventDefault()
        onRulerPointDelete(e.features[0].properties.index)
      })

      map.current.on('mousemove', 'ruler-path-points', () => {
        if (mapState().structDragActive) return
        if (!mapState().rulerModeActive) return
        map.current.getCanvas().style.cursor = 'grab'
      })
      map.current.on('mouseleave', 'ruler-path-points', () => {
        if (mapState().structDragActive) return
        if (!mapState().rulerModeActive) return
        map.current.getCanvas().style.cursor = 'crosshair'
      })
    } else {
      pointSrc.setData(pointFc)
    }
    if (map.current.getLayer('ruler-path-points')) {
      map.current.setLayoutProperty('ruler-path-points', 'visibility', hasPath ? 'visible' : 'none')
    }

    const labelSrc = map.current.getSource('ruler-segment-labels')
    if (!labelSrc) {
      map.current.addSource('ruler-segment-labels', { type: 'geojson', data: labelFc })
      map.current.addLayer({
        id: 'ruler-segment-labels',
        type: 'symbol',
        source: 'ruler-segment-labels',
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 11,
          'text-anchor': 'bottom',
          'text-offset': [0, -0.6],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#4FD0E0',
          'text-halo-color': '#1a1a1a',
          'text-halo-width': 1.4,
        },
      })
    } else {
      labelSrc.setData(labelFc)
    }
    if (map.current.getLayer('ruler-segment-labels')) {
      map.current.setLayoutProperty('ruler-segment-labels', 'visibility', hasPath ? 'visible' : 'none')
    }
  }, [map, rulerMode, rulerPath, onRulerPointMove, onRulerPointDelete])
}
